import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Skeleton,
  UserAvatar,
} from "@nymbl/nos2-rules";
import { useAvatarUrl } from "@/hooks/use-files";
import { useMyProfile } from "@/hooks/use-my-profile";
import { useSession } from "@/lib/auth-client";
import { avatarImage } from "@/lib/person-photo";
import { formatStartDate, roleLabel, tagLabel } from "@/lib/profile";

/**
 * The person's card from the warehouse, as My NOS's Profile shows it: photo, name, title,
 * department, manager, start date, roles and skill tags. Read-only: these are kept in NOS.
 */
export function PersonProfileCard() {
  const { data: session } = useSession();
  const query = useMyProfile();

  if (query.isError) {
    return (
      <ErrorState
        message="Your profile didn't load"
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (!query.data) {
    return (
      <div aria-busy="true">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const p = query.data;
  const fields: { label: string; value: React.ReactNode }[] = [
    { label: "Department", value: p.department ?? "—" },
    { label: "Manager", value: p.managerName ?? "—" },
    { label: "Start date", value: formatStartDate(p.startDate) ?? "—" },
    {
      label: "Roles",
      value:
        p.roles.length === 0 ? (
          "—"
        ) : (
          <ul aria-label="Roles" className="flex flex-wrap justify-end gap-1.5">
            {p.roles.map((r) => (
              <li key={`${r.role}-${r.level ?? ""}`}>
                <Badge variant={r.isPrimary ? "default" : "secondary"}>{roleLabel(r)}</Badge>
              </li>
            ))}
          </ul>
        ),
    },
    {
      label: "Skill tags",
      value:
        p.tags.length === 0 ? (
          "—"
        ) : (
          <ul aria-label="Skill tags" className="flex flex-wrap justify-end gap-1.5">
            {p.tags.map((t) => (
              <li key={t.tag}>
                <Badge variant="outline">{tagLabel(t)}</Badge>
              </li>
            ))}
          </ul>
        ),
    },
  ];

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center gap-4">
        <UserAvatar
          name={p.name}
          image={avatarImage(session?.user.image, p.photoUrl)}
          useImageUrl={useAvatarUrl}
          className="h-16 w-16 text-lg"
        />
        <div className="min-w-0">
          <CardTitle className="text-xl">
            <h2>{p.name}</h2>
          </CardTitle>
          {p.title && <p className="text-sm text-muted-foreground">{p.title}</p>}
        </div>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border/50">
          {fields.map((field) => (
            <div
              key={field.label}
              className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
            >
              <dt className="shrink-0 text-sm text-muted-foreground">{field.label}</dt>
              <dd className="min-w-0 text-right text-sm font-medium">{field.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
